import {
  assertValidStudioDocument,
  createStudioDocument,
} from "../../engines/StudioDocumentEngine";

import {
  projectRetailPOSWorkspaces,
} from "./retailStudioCapabilities";


export const RETAIL_STUDIO_DOMAIN_ID =
  "retail";


export const RETAIL_STUDIO_DOCUMENT_SCHEMA_VERSION =
  1;


/**
 * Create the declarative Retail extension carried by
 * a generic StudioDocument.
 *
 * RetailStudioDocument never owns dynamic business data.
 *
 * Products, inventory, customers, loyalty accounts,
 * carts, payments, receipts and cash sessions remain
 * owned by their dedicated engines.
 */
export function createRetailStudioConfiguration(
  overrides = {}
) {
  const catalog =
    normalizeCatalogConfiguration(
      overrides.catalog
    );

  return deepFreeze({
    storeIdentity:
      clonePlainObject(
        overrides.storeIdentity
      ),

    posWorkspaces:
      cloneArray(
        projectRetailPOSWorkspaces(
          overrides.posWorkspaces
        )
      ),

    catalog,

    scannerConfiguration:
      clonePlainObject(
        overrides
          .scannerConfiguration
      ),

    terminalConfiguration:
      clonePlainObject(
        overrides
          .terminalConfiguration
      ),

    customerActions:
      cloneArray(
        overrides.customerActions
      ),

    loyaltyActions:
      cloneArray(
        overrides.loyaltyActions
      ),

    paymentActions:
      cloneArray(
        overrides.paymentActions
      ),

    receiptActions:
      cloneArray(
        overrides.receiptActions
      ),

    stockWidgets:
      cloneArray(
        overrides.stockWidgets
      ),

    navigation:
      cloneArray(
        overrides.navigation
      ),

    theme:
      clonePlainObject(
        overrides.theme
      ),

    layout:
      clonePlainObject(
        overrides.layout
      ),

    permissionsMetadata:
      clonePlainObject(
        overrides
          .permissionsMetadata
      ),
  });
}


/**
 * Create one Retail Studio document using the generic
 * StudioDocument contract.
 *
 * Retail-specific configuration lives exclusively under:
 *
 * extensions.retail
 */
export function createRetailStudioDocument(
  overrides = {}
) {
  const source =
    isPlainObject(overrides)
      ? overrides
      : {};

  const documentId =
    normalizeDocumentId(
      source.uuid
      ?? source.id
      ?? "retail-studio-document"
    );

  const extensions =
    clonePlainObject(
      source.extensions
    );

  extensions.retail =
    createRetailStudioConfiguration(
      source.retail
      ?? extensions.retail
      ?? {}
    );

  const document =
    createStudioDocument(
      {
        schemaVersion:
          source.schemaVersion
          ?? RETAIL_STUDIO_DOCUMENT_SCHEMA_VERSION,

        uuid:
          documentId,

        studioType:
          RETAIL_STUDIO_DOMAIN_ID,

        name:
          typeof source.name === "string"
            && source.name.trim() !== ""
            ? source.name
            : "Retail Studio",

        canvas:
          source.canvas,

        spatial:
          source.spatial,

        scenes:
          Array.isArray(
            source.scenes
          )
            ? source.scenes
            : [],

        metadata: {
          ...clonePlainObject(
            source.metadata
          ),

          domainId:
            RETAIL_STUDIO_DOMAIN_ID,
        },

        extensions,
      },
      {
        createId:
          () => documentId,
      }
    );

  assertValidStudioDocument(
    document
  );

  return deepFreeze(
    document
  );
}


/**
 * Clone and normalize an existing Retail Studio document.
 */
export function cloneRetailStudioDocument(
  document
) {
  assertRetailStudioDocument(
    document
  );

  return createRetailStudioDocument(
    deepClone(document)
  );
}


/**
 * Return whether a value satisfies the Retail Studio
 * document contract.
 */
export function isRetailStudioDocument(
  document
) {
  try {
    assertRetailStudioDocument(
      document
    );

    return true;
  } catch {
    return false;
  }
}


/**
 * Assert the Retail Studio document contract.
 */
export function assertRetailStudioDocument(
  document
) {
  assertValidStudioDocument(
    document
  );

  if (
    document.metadata
      ?.domainId
      !== RETAIL_STUDIO_DOMAIN_ID
  ) {
    throw new TypeError(
      'RetailStudioDocument metadata.domainId must be "retail".'
    );
  }

  if (
    !isPlainObject(
      document.extensions
    )
    || !isPlainObject(
      document.extensions.retail
    )
  ) {
    throw new TypeError(
      "RetailStudioDocument requires extensions.retail."
    );
  }

  assertRetailConfiguration(
    document.extensions.retail
  );

  return document;
}


function assertRetailConfiguration(
  configuration
) {
  const arrayFields = [
    "posWorkspaces",
    "customerActions",
    "loyaltyActions",
    "paymentActions",
    "receiptActions",
    "stockWidgets",
    "navigation",
  ];

  const objectFields = [
    "storeIdentity",
    "catalog",
    "scannerConfiguration",
    "terminalConfiguration",
    "theme",
    "layout",
    "permissionsMetadata",
  ];

  for (
    const field
    of arrayFields
  ) {
    if (
      !Array.isArray(
        configuration[field]
      )
    ) {
      throw new TypeError(
        `RetailStudioDocument ${field} must be an array.`
      );
    }
  }

  for (
    const field
    of objectFields
  ) {
    if (
      !isPlainObject(
        configuration[field]
      )
    ) {
      throw new TypeError(
        `RetailStudioDocument ${field} must be a plain object.`
      );
    }
  }

  if (
    !Array.isArray(
      configuration
        .catalog.sections
    )
    || !Array.isArray(
      configuration
        .catalog
        .productShortcuts
    )
    || !Array.isArray(
      configuration
        .catalog
        .categoryShortcuts
    )
  ) {
    throw new TypeError(
      "RetailStudioDocument catalog lists must be arrays."
    );
  }
}


function normalizeCatalogConfiguration(
  catalog
) {
  const source =
    isPlainObject(catalog)
      ? catalog
      : {};

  return {
    ...deepClone(source),

    sections:
      cloneArray(
        source.sections
      ),

    productShortcuts:
      cloneArray(
        source
          .productShortcuts
      ),

    categoryShortcuts:
      cloneArray(
        source
          .categoryShortcuts
      ),
  };
}


function normalizeDocumentId(
  value
) {
  if (
    typeof value !== "string"
    || value.trim() === ""
  ) {
    return "retail-studio-document";
  }

  return value.trim();
}


function cloneArray(
  value
) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(
    (entry) =>
      deepClone(entry)
  );
}


function clonePlainObject(
  value
) {
  if (!isPlainObject(value)) {
    return {};
  }

  return deepClone(value);
}


function deepClone(
  value
) {
  if (Array.isArray(value)) {
    return value.map(
      deepClone
    );
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .map(
          ([key, entry]) => [
            key,
            deepClone(entry),
          ]
        )
    );
  }

  return value;
}


function deepFreeze(
  value
) {
  if (
    value === null
    || typeof value
      !== "object"
    || Object.isFrozen(value)
  ) {
    return value;
  }

  Object.freeze(value);

  Object.values(value)
    .forEach(
      deepFreeze
    );

  return value;
}


function isPlainObject(
  value
) {
  if (
    value === null
    || typeof value
      !== "object"
    || Array.isArray(value)
  ) {
    return false;
  }

  const prototype =
    Object.getPrototypeOf(value);

  return (
    prototype
      === Object.prototype
    || prototype === null
  );
}
